import React, { useMemo, useState } from 'react';
import managerRowStyles from './ManagerRow.module.scss';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import Person4OutlinedIcon from '@mui/icons-material/Person4Outlined';
import EmptyState from '../composites/EmptyState';
import ContentDrill from '../composites/ContentDrill';
import ManagerRow from './ManagerRow';
import ListContent from '../composites/ListContent';
import { getFullName } from '../../utils/dashboardUtils';
import AddManagerDialog from './AddManagerDialog';
import { useSessionData, useUserAction } from '../../providers';

import type { Manager, WithOptionalId } from '../../types';

const managerListTitles = ['Manager Name', 'Options'];

const Managers: React.FC = () => {
  const { managers: allGymManagers, gym, loginManager } = useSessionData();
  const { createManager, updateManager } = useUserAction();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedManager, setSelectedManager] = useState<string>('');
  const [isAddManagerModalOpened, setIsAddManagerModalOpened] = useState(false);

  const filteredManagers = useMemo(() => {
    if (!Object.values(allGymManagers).length) return [];
    const searchQueryRegex = new RegExp(searchQuery.toLowerCase());
    return Object.values(allGymManagers)
      .filter((manager) =>
        searchQueryRegex.test(getFullName(manager).toLowerCase()),
      )
      .sort(
        (a, b) =>
          a.firstName
            ?.toLowerCase()
            .localeCompare(b.firstName?.toLowerCase() || '') || 0,
      );
  }, [searchQuery, allGymManagers]);

  if (!loginManager || !gym) {
    return (
      <EmptyState icon={<Person4OutlinedIcon />} text="No managers found." />
    );
  }

  const renderEmptyState = () => (
    <EmptyState
      icon={<Person4OutlinedIcon />}
      cta={{
        icon: <Person4OutlinedIcon color="primary" />,
        label: 'Add A Manager',
        onClick: () => setIsAddManagerModalOpened(true),
      }}
      text="No Managers Have Been Added"
    />
  );

  const addManager = async (
    manager: WithOptionalId<Manager>,
  ): Promise<boolean> => {
    const success = await createManager(manager);
    if (!success) {
      return false;
    }
    setIsAddManagerModalOpened(false);
    return true;
  };

  const renderAddProgramDialog = () => (
    <AddManagerDialog
      onSubmit={addManager}
      onClose={() => setIsAddManagerModalOpened(false)}
      isOpen={isAddManagerModalOpened}
    />
  );

  const deleteManager = async (manager: Manager) => {
    await updateManager({ ...manager, isActive: false });
  };

  const renderContent = () => {
    return (
      <ListContent
        items={filteredManagers}
        listHeaderClassName={managerRowStyles.listGrid}
        EmptyStateComponent={renderEmptyState}
        titles={managerListTitles}
        ItemComponent={({ data }) => (
          <ManagerRow
            manager={data}
            deleteManager={deleteManager}
            selected={selectedManager === data.id}
            onSelect={() => setSelectedManager(data.id)}
            key={data.id}
          />
        )}
      />
    );
  };

  const getSearchPlaceholder = () => {
    if (!filteredManagers.length) return '';
    return 'Search for Managers';
  };

  return (
    <ContentDrill
      title="Managers"
      isParent
      actions={[
        {
          icon: <AddBoxOutlinedIcon />,
          label: 'Add A Manager',
          onClick: () => setIsAddManagerModalOpened(true),
        },
      ]}
      searchPlaceholder={getSearchPlaceholder()}
      setSearchQuery={setSearchQuery}
      disableSearch={false}
    >
      {renderContent()}
      {renderAddProgramDialog()}
    </ContentDrill>
  );
};

export default Managers;
